import { useCallback, useMemo } from 'react';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import {
  normalizeReportComponent,
  reportCanvasComponents,
  WORKBOOK_REPORT_CLIENT_ROW_LIMIT,
  type ReportCanvasLayout,
  type WorkbookDefinition,
  type WorkbookResultRows,
  type WorkbookRun,
} from '@erp/shared-types';
import { defaultReportDatasetKey, sheetFingerprints } from '@erp/workbooks';
import { orpc } from '@/api/rpc/client';
import type { ReportDataset } from '@/components/report-canvas/ReportDataContext';
import { workbookRunResults } from './workbookRunResults';
import {
  workbookReportColumns,
  workbookReportRows,
  workbookReportSheetColumns,
  workbookReportSheets,
  type WorkbookReportSheet,
} from './workbookReportDataset';

/**
 * How a Report dataset gets its rows: `none` without a Result to chart,
 * `client` with every row in the browser, `server` from Server aggregation
 * (past the Client row limit).
 */
export type WorkbookReportPath = 'none' | 'client' | 'server';

/** A Report dataset's Worksheet with the Result it charts. */
export type WorkbookReportSource = WorkbookReportSheet & {
  /** The completed Run holding the Result: its newest Current one, else the newest. */
  runId: string | undefined;
  ranAt: string | undefined;
  rowCount: number | undefined;
  /** The Result's Fingerprint matches the Definition's; otherwise it is Stale. */
  current: boolean;
  path: WorkbookReportPath;
};

/**
 * Each Report dataset's Worksheet and the Result it charts, chosen as the
 * Worksheet tab chooses its Result. A Result past the Client row limit is
 * charted from Server aggregation; the server may also send one within it
 * that way when its rows are too many bytes.
 */
export function workbookReportSources(
  definition: WorkbookDefinition,
  runs: readonly WorkbookRun[] | undefined,
): WorkbookReportSource[] {
  const results = workbookRunResults(runs, sheetFingerprints(definition));
  return workbookReportSheets(definition).map((entry) => {
    const { run, result, current } = results.sheet(entry.sheet.id);
    const ran = result ? run : undefined;
    return {
      ...entry,
      columns: workbookReportSheetColumns(entry.sheet, result?.columns),
      runId: ran?.id,
      ranAt: ran?.createdAt,
      rowCount: result?.rowCount,
      current: !!result && current,
      path: reportSourcePath(result?.rowCount),
    };
  });
}

/** How a Result is charted: not yet (no Result), in the browser, or past the Client row limit from Server aggregation. */
export function reportSourcePath(
  rowCount: number | undefined,
): WorkbookReportSource['path'] {
  if (rowCount === undefined) return 'none';
  return rowCount > WORKBOOK_REPORT_CLIENT_ROW_LIMIT ? 'server' : 'client';
}

/** The Report dataset charted when a component names none: the first Worksheet's, else none (''). */
export function reportDefaultDatasetKey(definition: {
  sheets: readonly { id: string }[];
}): string {
  return defaultReportDatasetKey(definition) ?? '';
}

/**
 * The Report datasets a layout's components chart; for an empty layout, the
 * default one, so the first chart dropped has rows.
 */
export function reportLayoutDatasetKeys(
  layout: ReportCanvasLayout | undefined,
  defaultKey: string,
): Set<string> {
  const components = layout ? reportCanvasComponents(layout) : [];
  return new Set(
    components.length
      ? components.map(
          // As the canvas hydrates it: a datasetKey nested under config counts.
          (component) =>
            normalizeReportComponent(component).datasetKey ?? defaultKey,
        )
      : [defaultKey],
  );
}

type LoadedRows = Extract<WorkbookResultRows, { kind: 'rows' }>;
// Rows converted once per read and Worksheet columns: unrelated edits keep both.
const convertedRows = new WeakMap<
  LoadedRows,
  {
    columns: WorkbookReportSource['columns'];
    rows: ReportDataset['rows'];
  }
>();
function datasetRows(
  sheet: Pick<WorkbookReportSource, 'columns'>,
  loaded: LoadedRows,
): ReportDataset['rows'] {
  const cached = convertedRows.get(loaded);
  if (cached?.columns === sheet.columns) return cached.rows;
  const rows = workbookReportRows(sheet, loaded.columns, loaded.rows);
  convertedRows.set(loaded, { columns: sheet.columns, rows });
  return rows;
}

export type WorkbookReportData = {
  workbookId: string;
  datasets: ReportDataset[];
  defaultDatasetKey: string;
  /** By Report dataset key. */
  sources: ReadonlyMap<string, WorkbookReportSource>;
};

/**
 * The Report datasets of a Workbook: every Worksheet, typed by
 * the Definition. Rows are read only for the datasets in `keys`, once per Run
 * (a Result never changes); a Result read past the Client row limit leaves its
 * dataset's rows to Server aggregation.
 */
export function useWorkbookReportData({
  workbookId,
  definition,
  runs,
  keys,
}: {
  workbookId: string;
  definition: WorkbookDefinition;
  runs: readonly WorkbookRun[] | undefined;
  keys: ReadonlySet<string>;
}): WorkbookReportData {
  const planned = useMemo(
    () => workbookReportSources(definition, runs),
    [definition, runs],
  );
  const read = useMemo(
    () =>
      planned.filter(
        (source) => keys.has(source.key) && source.path === 'client',
      ),
    [planned, keys],
  );
  const combine = useCallback(
    (queries: UseQueryResult<WorkbookResultRows>[]) =>
      queries.map((query) => query.data),
    [],
  );
  const rows = useQueries({
    queries: read.map((source) =>
      orpc.workbooks.resultRows.queryOptions({
        input: {
          workbookId,
          runId: source.runId ?? '',
          sheetId: source.sheet.id,
        },
        staleTime: Infinity,
      }),
    ),
    combine,
  });
  return useMemo(() => {
    const byKey = new Map(
      read.map((source, index) => [source.key, rows[index]]),
    );
    const sources = new Map(
      planned.map((source) => {
        const loaded = byKey.get(source.key);
        return [
          source.key,
          loaded?.kind === 'server'
            ? { ...source, path: 'server' as const }
            : source,
        ];
      }),
    );
    const datasets = planned.map((source): ReportDataset => {
      const loaded = byKey.get(source.key);
      return {
        key: source.key,
        label: source.label,
        columns: workbookReportColumns(source),
        rows: loaded?.kind === 'rows' ? datasetRows(source, loaded) : [],
        isLoading: byKey.has(source.key) && !loaded,
      };
    });
    return {
      workbookId,
      datasets,
      defaultDatasetKey: reportDefaultDatasetKey(definition),
      sources,
    };
  }, [workbookId, planned, read, rows, definition]);
}
